import { useCallback, useMemo, useState } from 'react';

import { useDatabaseContext } from '../screens/DatabaseContextProvider';
import { useTogglDataContext } from '../screens/TogglDataProvider';
import { deleteImportMapping, putImportMappings } from '../services/api';
import { getErrorMessage } from '../services/helpers';
import { deleteWorklog, showFlag } from '../services/jira';
import { ImportMapping } from '../types';
import { TogglTimeEntry } from '../types/toggl-api';
import {
  ADD_WORKLOG_ERROR_REASONS,
  createWorklog,
  CreateWorklogResponse,
} from '../utils/jiraWorklogs';
import { chunkArray } from '../utils/utils';
import { createNewImportMapping } from '../views/configuration/my-entries/hooks/useSelectedTimeEntries';

interface SyncEntry {
  issueKey: string;
  timeEntry: TogglTimeEntry;
}

export const useSyncUtils = () => {
  const {
    data: { importMappings, globalConfiguration },
    updateContext,
  } = useDatabaseContext();
  const { timezone } = useTogglDataContext();
  const [progressPoints, setProgressPoints] = useState(0);
  const [maxProgressPoints, setMaxProgressPoints] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const setImportMappings = updateContext('importMappings');

  const syncEntriesChunk = useCallback(
    async (chunk: CreateWorklogResponse[]) => {
      let unexpectedErrorsCount = 0;
      let wrongIssueKeysCount = 0;
      const createdWorklogs = (await Promise.all(chunk)).filter(Boolean);
      setProgressPoints((p) => p + 2);
      const newMappings: Record<string, ImportMapping> = {};
      createdWorklogs.forEach((w) => {
        if ('error' in w) {
          if (w.error === ADD_WORKLOG_ERROR_REASONS.UNEXPECTED_ERROR) {
            unexpectedErrorsCount++;
          } else if (w.error === ADD_WORKLOG_ERROR_REASONS.WRONG_ISSUE_KEY) {
            wrongIssueKeysCount++;
          }
        } else if (w) {
          newMappings[w.timeEntry.id] = createNewImportMapping(w);
        }
      });
      const newMappingsArray: ImportMapping[] = Object.values(newMappings);
      try {
        if (newMappingsArray.length) {
          await putImportMappings(newMappingsArray);
          setImportMappings((prev) => ({
            ...prev,
            ...newMappings,
          }));
        }
      } catch {
        wrongIssueKeysCount = 0;
        unexpectedErrorsCount = chunk.length;
      }
      setProgressPoints((p) => p + 1);
      return { wrongIssueKeysCount, unexpectedErrorsCount };
    },
    [setProgressPoints, setProgressPoints, setImportMappings, importMappings]
  );

  const syncEntries = useCallback(
    async (entries: SyncEntry[]) => {
      setIsLoading(true);
      const promises = entries.map(
        (entry) => () =>
          createWorklog({
            ...entry,
            togglTimeZone: timezone,
            jiraTimeZone: globalConfiguration?.jiraTimeZone,
            roundTime: Boolean(globalConfiguration?.roundTime),
          })
      );
      let unexpectedErrorsCount = 0;
      let wrongIssueKeysCount = 0;
      const promisesChunks = chunkArray(promises, 10);
      setMaxProgressPoints(promisesChunks.length * 3);
      for (const chunk of promisesChunks) {
        const result = await syncEntriesChunk(chunk.map((cb) => cb()));
        unexpectedErrorsCount += result.unexpectedErrorsCount;
        wrongIssueKeysCount += result.wrongIssueKeysCount;
      }
      setIsLoading(false);
      setProgressPoints(0);
      return { unexpectedErrorsCount, wrongIssueKeysCount };
    },
    [timezone, globalConfiguration, setIsLoading, syncEntriesChunk]
  );

  const syncOneEntry = useCallback(
    async (entry: SyncEntry) => {
      const { unexpectedErrorsCount, wrongIssueKeysCount } = await syncEntries([
        entry,
      ]);
      if (unexpectedErrorsCount) {
        showFlag(
          'Failed to sync time entry, caused by unexpected error',
          '',
          'error',
          'manual'
        );
      } else if (wrongIssueKeysCount) {
        showFlag(
          `Cannot find issue with key: ${entry.issueKey}`,
          '',
          'error',
          'manual'
        );
      } else {
        showFlag('Time entry synced', '', 'success');
      }
      setProgressPoints(0);
      return { failedCount: unexpectedErrorsCount + wrongIssueKeysCount };
    },
    [setProgressPoints, syncEntries]
  );

  const unsyncEntry = useCallback(
    async ({
      jiraWorklogId,
      togglTimeEntryId,
      jiraIssueKey,
    }: ImportMapping) => {
      setIsLoading(true);
      try {
        await deleteWorklog(jiraIssueKey, jiraWorklogId);
        await deleteImportMapping(togglTimeEntryId);
        setImportMappings((prev) => ({
          ...prev,
          [togglTimeEntryId]: null,
        }));
        showFlag('Entry successfully unsynced', '', 'success');
      } catch (error) {
        console.error('Error while removing worklog', getErrorMessage(error));
        showFlag('Failed to unsync', '', 'error');
      }
      setIsLoading(false);
    },
    [setImportMappings, setIsLoading, importMappings]
  );

  const progress = useMemo(
    () => progressPoints / maxProgressPoints,
    [progressPoints, maxProgressPoints]
  );

  return { syncEntries, progress, syncOneEntry, isLoading, unsyncEntry };
};
